import { useMutation, useQuery, UseMutationResult, useQueryClient } from "@tanstack/react-query";
import { createMilestoneTasks, getMilestonesTasks, updateMilestoneTasks } from "../../../api/homeSiteMilestones/mileStoneTasksApi";
import { MilestoneTask, MilestoneTasks } from "../../../api/homeSiteMilestones/types";
import React, { useMemo } from "react";
import { getHomeSiteTaskMetrics } from "../../../utils/homeSiteTaskMetrics/homeSiteTaskMetrics";
import { DivisionMileStones } from "../../../api/divisionMileStones/types";
import { HomeSitesDataIndex } from "../../homeSitesDataIndex/types";

export const useMileStoneTasksData = (divCode: string) => {
     return useQuery<MilestoneTasks>({
          queryKey: ["milestoneTasks", divCode],
          queryFn: () =>
               getMilestonesTasks(
                    { filter: `divisionCode=${divCode},completionDatenotIn(null),closeOfEscrowActualin(null),docStatus=true` },
                    { filter: `dueDatenotIn(null),saleDatenotIn(null)` }
               ),
          enabled: !!divCode,
          staleTime: 1000 * 60 * 15, // 15 minutes
          gcTime: 1000 * 60 * 15, // 15 minutes
          refetchInterval: 1000 * 60 * 15, // Refetch every 15 minutes
          refetchIntervalInBackground: true,
     });
};

export const useCreateMilestoneTasks = () => {
     const queryClient = useQueryClient();
     return useMutation<MilestoneTask, unknown, MilestoneTask, unknown>({
          mutationFn: createMilestoneTasks as any as (data: MilestoneTask) => Promise<MilestoneTask>,
          onSuccess: (data) => {
               queryClient.setQueryData(["milestoneTasks", data.divisionCode], (oldData: any) => {
                    if (!oldData) return;
                    return { ...oldData, data: [...oldData.data, data] };
               });
               queryClient.setQueryData(["milestoneTasksHomesite", data.divisionCode, data.homeSiteId], (oldData: any) => {
                    if (!oldData) return;
                    return { ...oldData, data: [...oldData.data, data] };
               });
               queryClient.setQueryData(["allMilestoneTasks", data.divisionCode], (oldData: any) => {
                    if (!oldData) return;
                    return { ...oldData, data: [...oldData.data, data] };
               });
          },
     });
};

export const useMileStoneTasksIncrementalData = (divCode: string, continuationToken: string) => {
     const queryClient = useQueryClient();
     const [isLoading, setIsLoading] = React.useState(false);

     React.useEffect(() => {
          if (!divCode || !continuationToken) return;
          const prefetch = async (initialContinuationToken: string) => {
               setIsLoading(true);
               let token = initialContinuationToken;
               while (token) {
                    const currentToken = token;
                    const newContent = await queryClient.fetchQuery<MilestoneTasks>({
                         queryKey: ["milestoneTasks", divCode, currentToken],
                         queryFn: () =>
                              getMilestonesTasks(
                                   { filter: `divisionCode=${divCode},completionDatenotIn(null),closeOfEscrowActualin(null),docStatus=true`, continuationToken: currentToken, },
                                   { filter: `dueDatenotIn(null),saleDatenotIn(null)` }
                              ),
                    });
                    queryClient.setQueryData<MilestoneTasks>(["milestoneTasks", divCode], (oldData) => {
                         if (!oldData) return newContent;
                         const updatedTasks = oldData?.data?.map((task: MilestoneTask) => {
                              if (task.completionDate === null && task.dueDate === null) {
                                   return task;
                              }
                              const updatedTask = newContent?.data?.find((updated: MilestoneTask) => updated.homeSiteId === task.homeSiteId && updated.taskName === task.taskName);
                              if (updatedTask) {
                                   if (updatedTask.completionDate === null && updatedTask.dueDate === null) {
                                        return updatedTask;
                                   }
                                   newContent.data = newContent.data.filter((t) => t.id !== updatedTask.id);
                              }
                              return updatedTask !== undefined ? updatedTask : task;
                         });
                         if (newContent.data) {
                              updatedTasks.push(...newContent.data);
                         }
                         return {
                              ...oldData,
                              data: updatedTasks,
                              counts: updatedTasks.length,
                              totalcounts: updatedTasks.length,
                              hasMoreResults: newContent.hasMoreResults,
                              continuationToken: newContent.continuationToken,
                         }
                    });
                    token = newContent.continuationToken ?? "";
               };
               setIsLoading(false);
          };

          prefetch(continuationToken);
     }, [continuationToken, divCode, queryClient]);

     return { isLoading };
};

export const useAllMilestoneTasksData = (divCode: string) => {
     const queryClient = useQueryClient();
     const queryKey = ["allMilestoneTasks", divCode];

     const { data, ...rest } = useQuery<MilestoneTasks>({
          queryKey,
          queryFn: () => getMilestonesTasks({ filter: `divisionCode=${divCode},docStatus=true` }),
          enabled: !!divCode,
          staleTime: 1000 * 60 * 15,
          gcTime: 1000 * 60 * 15,
     });

     React.useEffect(() => {
          if (!divCode || !data?.continuationToken) return;

          const fetchAllPages = async (initialToken: string) => {
               let token = initialToken;
               while (token) {
                    const page = await queryClient.fetchQuery<MilestoneTasks>({
                         queryKey: [...queryKey, token],
                         queryFn: () => getMilestonesTasks({
                              filter: `divisionCode=${divCode},docStatus=true`,
                              continuationToken: token,
                         }),
                    });

                    queryClient.setQueryData<MilestoneTasks>(queryKey, (oldData) => {
                         if (!oldData) return page;
                         const existingIds = new Set(oldData.data.map(t => t.id));
                         const newTasks = page.data.filter(t => !existingIds.has(t.id));
                         const merged = [...oldData.data, ...newTasks];
                         return {
                              ...oldData,
                              data: merged,
                              counts: merged.length,
                              totalcounts: merged.length,
                              hasMoreResults: page.hasMoreResults,
                              continuationToken: page.continuationToken,
                         };
                    });

                    token = page.continuationToken ?? "";
               }
          };

          fetchAllPages(data.continuationToken);
     }, [divCode, data?.continuationToken, queryClient, queryKey]);

     return { data, ...rest };
};

export const useMileStonTaksDataByHomesiteId = (divCode: string, homesiteId: string) => {
     return useQuery({
          queryKey: ["milestoneTasksHomesite", divCode, homesiteId],
          queryFn: () =>
               getMilestonesTasks({
                    filter: `divisionCode=${divCode},homeSiteId=${homesiteId},docStatus=true`,
               }),
          enabled: !!divCode && !!homesiteId,
          staleTime: 1000 * 60 * 15, // 15 minutes
          gcTime: 1000 * 60 * 15, // 15 minutes
          refetchInterval: 1000 * 60 * 15, // 15 minutes
          refetchIntervalInBackground: true,
     });
};

export const useMileStonTaksCountsData = (divCode: string) => {
     return useQuery({
          queryKey: ["milestoneTasksCounts", divCode],
          queryFn: () =>
               getMilestonesTasks(
                    {
                         filter: `divisionCode=${divCode},status=true,docStatus=true`,
                         fields: "homeSiteId",
                         groupBy: "milestoneName",
                    }),
          enabled: !!divCode,
          staleTime: 1000 * 60 * 15, // 15 minutes
          gcTime: 1000 * 60 * 15, // 15 minutes
          refetchInterval: 1000 * 60 * 15, // 15 minutes
          refetchIntervalInBackground: true,
     });
};


const updateData = (oldData: any, updatedTasks: MilestoneTask[]) => {
     if (!oldData) return;
     const newData = {
          ...oldData,
          data: oldData?.data?.map((task: MilestoneTask) => {
               const updatedTask = updatedTasks.find((updated: MilestoneTask) => updated.homeSiteId === task.homeSiteId && updated.taskName === task.taskName);
               if (updatedTask) {
                    updatedTasks = updatedTasks.filter((t) => t.id !== updatedTask.id);
               }
               return updatedTask !== undefined ? updatedTask : task;
          }),
     };
     if (updatedTasks.length > 0) {
          newData.data.push(...updatedTasks);
          newData.counts = newData.data.length;
          newData.totalcounts = newData.data.length;
     }
     return newData;
};

export const useUpdateMilestoneTasksById = (divCode: string, homesiteId: string): UseMutationResult<MilestoneTask[], unknown, Partial<MilestoneTask>[], unknown> => {
     const queryClient = useQueryClient();
     return useMutation<MilestoneTask[], unknown, Partial<MilestoneTask>[], unknown>({
          mutationFn: updateMilestoneTasks,
          onSuccess: (updatedTasks: MilestoneTask[]) => {
               queryClient.setQueryData(["milestoneTasksHomesite", divCode, homesiteId], (oldData: any) => {
                    if (!oldData) return;
                    return {
                         ...oldData,
                         data: oldData?.data?.map((task: MilestoneTask) => {
                              const updatedTask = updatedTasks?.find((updated: MilestoneTask) => updated.id === task.id);
                              return updatedTask !== undefined ? updatedTask : task;
                         }),
                    };
               });
               queryClient.setQueryData(["milestoneTasks", divCode], (oldData: any) => {
                    if (!oldData) return;
                    return updateData(oldData, updatedTasks);
               });
               queryClient.setQueryData(["allMilestoneTasks", divCode], (oldData: any) => {
                    if (!oldData) return;
                    return updateData(oldData, [...updatedTasks]);
               });
          },
          onError: (error) => {
          },
     });
};

export const useCreateMilestoneTask = (divCode: string) => {
     const queryClient = useQueryClient();
     return useMutation<MilestoneTask, unknown, Partial<MilestoneTask>, unknown>({
          mutationFn: createMilestoneTasks,
          onSuccess: (data) => {
               queryClient.setQueryData(["milestoneTasks", divCode], (oldData: any) => {
                    if (!oldData) return;
                    return { ...oldData, data: [...oldData.data, data] };
               });
               queryClient.setQueryData(["milestoneTasksHomesite", divCode, data.homeSiteId], (oldData: any) => {
                    if (!oldData) return;
                    return { ...oldData, data: [...oldData.data, data] };
               });
               queryClient.setQueryData(["allMilestoneTasks", divCode], (oldData: any) => {
                    if (!oldData) return;
                    return { ...oldData, data: [...oldData.data, data] };
               });
          },
          onError: (error) => {
          },
     });
};

export const useHomeSiteTasksObserver = (homeSites: HomeSitesDataIndex[], tasks: MilestoneTask[], divTasks: DivisionMileStones, msLoading: boolean) => {
     const metrics = useMemo(() => getHomeSiteTaskMetrics(divTasks, tasks, homeSites, msLoading), [divTasks, tasks, homeSites, msLoading]);
     return { metrics };
};
